import {
  PipelineGraph,
  useCreatePipelineGraphMutation,
  useDeletePipelineGraphMutation,
  useUpdatePipelineGraphMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import { ResultTypeFrom } from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";

export type UsePipelineGraphMutationProps = {}

export type UsePipelineGraphMutationResult = {
  createPipelineGraph: (pipelineGraph: Partial<PipelineGraph>, onSuccess: (() => void) | undefined) => void
  updatePipelineGraph: (pipelineGraph: Partial<PipelineGraph>, onSuccess: (() => void) | undefined) => void
  deletePipelineGraph: (pipelineId: string, onSuccess: (() => void) | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const usePipelineGraphMutation = (props: UsePipelineGraphMutationProps = {}): UsePipelineGraphMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createPipelineGraph, {isLoading: isCreating}] = useCreatePipelineGraphMutation()
  const [_updatePipelineGraph, {isLoading: isUpdating}] = useUpdatePipelineGraphMutation()
  const [_deletePipelineGraph, {isLoading: isDeleting}] = useDeletePipelineGraphMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "usePipelineGraphUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.pipelinegraph", "Pipeline Graph"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("usePipelineGraphUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "usePipelineGraphUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.pipelinegraph", "Pipeline Graph"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("usePipelineGraphUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "usePipelineGraphUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.pipelinegraph", "Pipeline Graph"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("usePipelineGraphUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.pipelinegraph", "Pipeline Graph") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.pipelinegraph", "Pipeline Graph") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.pipelinegraph", "Pipeline Graph") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteConfirm = useCallback((pipelineId: string, onSuccess: (() => void) | undefined = undefined) => {
    const promise = _deletePipelineGraph({id: pipelineId}).unwrap()
    handleDeleteResult(promise, onSuccess)
  }, [_deletePipelineGraph, handleDeleteResult])

  const createPipelineGraph = useCallback((pipelineGraph: Partial<PipelineGraph>, onSuccess: (() => void) | undefined) => {
    const promise = _createPipelineGraph(pipelineGraph).unwrap()
    handleCreateResult(promise, onSuccess)
  }, [_createPipelineGraph, handleCreateResult])

  const updatePipelineGraph = useCallback((pipelineGraph: Partial<PipelineGraph>, onSuccess: (() => void) | undefined) => {
    const promise = _updatePipelineGraph(pipelineGraph).unwrap()
    handleUpdateResult(promise, onSuccess)
  }, [_updatePipelineGraph, handleUpdateResult])

  const deletePipelineGraph = useCallback((pipelineId: string, onSuccess: (() => void) | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.pipelinegraph", "Pipeline Graph"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.deletepipelinegraph.confirm", "Are you sure you want to delete this pipeline graph?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(pipelineId, onSuccess)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createPipelineGraph: createPipelineGraph,
    updatePipelineGraph: updatePipelineGraph,
    deletePipelineGraph: deletePipelineGraph,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
